import {
  useEffect,
  useMemo,
  useRef,
  useState,
  useCallback,
  type ComponentProps
} from 'react';
import { useParams } from 'react-router-dom';
import {
  EditorShell,
  EditorTitle,
  EmptyState,
  FormattingToolbar,
  InspectorTabs,
  MarkdownEditor,
  type MarkdownEditorRef,
  Savebar,
  type SavebarMode,
  SheetSection,
  Skeleton,
  Toast
} from '../components/primitives';
import { supabase } from '../lib/supabase';
import { useActiveOrg } from '../lib/activeOrg';
import type { ActiveFormats } from '../lib/markdownFormatting';
import { PropertiesPanel } from '../components/documentEditor/PropertiesPanel';
import { useDocumentProperties } from '../components/documentEditor/useDocumentProperties';
import { AttachedWorkItemsPanel } from '../components/documentEditor/AttachedWorkItemsPanel';
import { DocumentProjectsSection } from '../components/documentEditor/DocumentProjectsSection';
import { HistoryDrawer } from '../components/HistoryDrawer';
import {
  CommentsPanel,
  type CommentRow,
  type CommentsMember
} from '../components/CommentsPanel';
import { UNTITLED_DOCUMENT_TITLE } from '../lib/editor/defaultTitles';
import { useDocumentEditorState } from '../hooks/useDocumentEditorState';
import { useVersionHistoryVM } from '../lib/vm/useVersionHistoryVM';
import { useRealtimeRowReload } from '../hooks/useRealtimeRowReload';
import { useTrackOpenItem } from '../hooks/useTrackOpenItem';
import { isIDE } from '../lib/isIDE';
import { useAgentWriteSubscription } from '../hooks/useAgentWriteSubscription';
import { AgentWriteToast } from '../components/agent/AgentWriteToast';
import { AgentArchiveBanner } from '../components/agent/AgentArchiveBanner';
import type { McpWriteCommittedFrame } from '../lib/agentProtocol';
import './DocumentEditor.css';

interface RawComment {
  id: string;
  parentCommentId: string | null;
  authorId: string;
  body: string;
  createdAt: string;
  editedAt: string | null;
  resolvedAt: string | null;
}

export interface DocumentEditorViewProps {
  /** Document id to load. */
  documentId: string | undefined;
  /** `'route'` = standalone page; `'cover'` = embedded in a FullScreenCover. */
  surface?: 'route' | 'cover';
  /** Cover mode: report unsaved-changes state so the host can guard close. */
  onDirtyChange?: (dirty: boolean) => void;
}

/**
 * The document editor body. Rendered as a standalone page by `DocumentEditor`
 * (route) and embedded inside a `FullScreenCover` by `EditorCover` (cover).
 */
export function DocumentEditorView({
  documentId,
  surface = 'route',
  onDirtyChange
}: DocumentEditorViewProps) {
  const id = documentId;
  // Active org scopes the restore-sync refetch below (the loaded doc itself
  // is already org-gated by useDocumentEditorState).
  const { activeOrgId } = useActiveOrg();

  const [toast, setToast] = useState<string | null>(null);
  // The editor stage is the scrolling column (`.pn-editor-shell-stage`); the
  // reconcile uses it to snapshot + restore scroll/caret around the inline
  // content swap so an agent write never jumps the document. Held as a DOM ref
  // on the shell so the hook can read the live stage node on demand.
  const shellRef = useRef<HTMLElement | null>(null);
  const getViewportAnchor = useCallback(
    () =>
      shellRef.current?.querySelector<HTMLElement>('.pn-editor-shell-stage') ??
      null,
    []
  );
  const editor = useDocumentEditorState({
    documentId: id,
    onError: setToast,
    getViewportAnchor
  });
  const {
    load,
    titleDraft,
    setTitleDraft,
    bodyDraft,
    setBodyDraft,
    saveState,
    dirty,
    onSave,
    applyRestoredVersion,
    reloadFromServer,
    historyTick
  } = editor;

  // Cover host guards close on unsaved changes; report dirty up.
  useEffect(() => {
    onDirtyChange?.(dirty);
  }, [dirty, onDirtyChange]);

  // Record the open document in the cross-route store so the standalone Agent
  // panel reports it as active context. This is the OPENED item (route id), not
  // an edit-driven value — see openItemStore / useTrackOpenItem. The title is
  // the LOADED title (not the editable draft), so it reflects what the user
  // opened; it resolves a moment after the id once the doc loads.
  const openItemTitle = load.kind === 'ready' ? load.doc.title : undefined;
  useTrackOpenItem('document', id, openItemTitle);

  const historyVM = useVersionHistoryVM('document', id);

  // A save in `useDocumentEditorState` mints a fresh `version_snapshots` row
  // and bumps `historyTick`. The History drawer's VM only loads on mount, so
  // without this it never reflects post-load saves — reload it on every bump.
  // `historyTick` starts at 0; the effect runs once on mount (a harmless
  // no-op reload) and then on each subsequent save.
  useEffect(() => {
    historyVM.actions.reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [historyTick]);

  // When a restore mints a new snapshot, the live doc body/title changed
  // server-side — refresh the editor drafts so they reflect the restore.
  // The VM loads snapshots on mount, so restoreSyncTick transitions
  // null -> <id> on first load; that initial value must NOT trigger a
  // refetch (it would clobber unsaved drafts in a load-vs-edit race).
  // restoreSyncSeenRef records the first observed tick and we only act on
  // a change that happens after it.
  const restoreSyncTick =
    historyVM.snapshots.find((s) => s.sourceVersionId != null)?.id ?? null;
  const restoreSyncSeenRef = useRef<string | null | undefined>(undefined);
  useEffect(() => {
    if (restoreSyncSeenRef.current === undefined) {
      // First run — record the baseline tick, do not act on initial load.
      restoreSyncSeenRef.current = restoreSyncTick;
      return;
    }
    if (restoreSyncTick === restoreSyncSeenRef.current) return;
    restoreSyncSeenRef.current = restoreSyncTick;
    if (restoreSyncTick == null || !id || load.kind !== 'ready') return;
    if (!activeOrgId) return;
    void (async () => {
      const { data } = await supabase
        .from('documents')
        .select('title, body, last_event_at')
        .eq('id', id)
        .eq('org_id', activeOrgId)
        .maybeSingle();
      if (!data) return;
      applyRestoredVersion({
        title: data.title ?? '',
        body: data.body ?? '',
        lastEventAt: data.last_event_at
      });
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [restoreSyncTick, activeOrgId]);

  const [historyOpen, setHistoryOpen] = useState(false);
  const [rawComments, setRawComments] = useState<RawComment[]>([]);
  const [commentsTick, setCommentsTick] = useState(0);
  const [members, setMembers] = useState<CommentsMember[]>([]);
  const [currentUserId, setCurrentUserId] = useState<string>('');

  // V2 relayout state — editor mode (Edit | Preview), right-rail inspector
  // open state (defaults to open so the panel is visible on first paint), and
  // active inspector tab. The MarkdownEditor ref lets the floating
  // FormattingToolbar splice markdown without holding the textarea ref.
  const [editorMode, setEditorMode] = useState<SavebarMode>('edit');
  const [railOpen, setRailOpen] = useState(false);
  const [inspectorTab, setInspectorTab] = useState<
    'properties' | 'work-items' | 'comments'
  >('properties');
  const bodyRef = useRef<MarkdownEditorRef | null>(null);
  const [activeFormats, setActiveFormats] = useState<ActiveFormats>({});

  const ideBuild = isIDE();

  // Phase 3, Task 3.2: agent-write reactions for the OPEN document.
  // `agentToastShown` drives the bottom-right `<AgentWriteToast>` after a
  // committed update to this doc; `agentArchived` flips true after a committed
  // delete, swapping the title bar for a non-dismissible archive banner.
  const [agentToastShown, setAgentToastShown] = useState(false);
  const [agentArchived, setAgentArchived] = useState(false);

  // The committed-write handler for THIS document (the hook has already
  // filtered to our kind+id, so every frame here targets the open doc).
  //
  //  - write/update + targetMatchesActive → show the update toast; reload from
  //    the server ONLY when the editor is NOT dirty. Reloading while dirty
  //    would silently clobber the user's unsaved edits — the data-loss guard.
  //  - create → ignored here (a provider-level handler dispatches that toast).
  //  - delete → raise the archive banner; the router must NOT auto-navigate.
  const onAgentWrite = useCallback(
    (frame: McpWriteCommittedFrame) => {
      switch (frame.tool) {
        // write_document / update_document / set_document_property all reconcile
        // identically: a property edit (lifecycle / owner / DRI / subtype /
        // custom) committed by the agent surfaces the toast and reloads the open
        // editor so the change reflects inline, exactly like a body update.
        case 'write_document':
        case 'update_document':
        case 'set_document_property': {
          if (!frame.targetMatchesActive) return;
          setAgentToastShown(true);
          if (!dirty) void reloadFromServer();
          return;
        }
        case 'delete_document': {
          setAgentArchived(true);
          return;
        }
        // create_document (and any unrelated tool) is a no-op at the editor
        // level — the provider owns the create toast (Task 3.4).
        default:
          return;
      }
    },
    [dirty, reloadFromServer]
  );

  // Source-agnostic live reconcile. Subscribe to Supabase Realtime changes on
  // the OPEN document's row and reconcile inline — works for ANY external
  // writer (real agent CLI, a second tab, a server-side RPC), not just writes
  // that flow through the IDE's WebSocket frame bus. NOT gated on isIDE().
  //
  // `dirty` is read through a ref so the subscription does not churn on every
  // keystroke; the realtime callbacks mirror the agent-frame handler exactly:
  //  - external UPDATE → surface the update toast; reload from the server ONLY
  //    when the editor is NOT dirty (reloadFromServer also defers an in-flight
  //    save and resets drafts wholesale, so guarding on !dirty is the
  //    unsaved-edit/caret data-loss guard).
  //  - external DELETE → raise the non-dismissible archive banner.
  const dirtyRef = useRef(dirty);
  dirtyRef.current = dirty;
  const onRealtimeChange = useCallback(() => {
    setAgentToastShown(true);
    if (!dirtyRef.current) void reloadFromServer();
  }, [reloadFromServer]);
  const onRealtimeDelete = useCallback(() => {
    setAgentArchived(true);
  }, []);
  useRealtimeRowReload({
    table: 'documents',
    rowId: load.kind === 'ready' ? load.doc.id : null,
    onChange: onRealtimeChange,
    onDelete: onRealtimeDelete
  });

  const openInspector = () => {
    setHistoryOpen(false);
    setRailOpen(true);
  };
  const closeInspector = () => setRailOpen(false);
  const openHistory = () => {
    setRailOpen(false);
    setHistoryOpen(true);
  };
  const closeHistory = () => setHistoryOpen(false);
  const toggleInspector = () =>
    railOpen ? closeInspector() : openInspector();
  const toggleHistory = () => (historyOpen ? closeHistory() : openHistory());

  // Load workspace members + current user. Reused by Comments author lookup
  // and by History row/event actor lookup.
  useEffect(() => {
    if (load.kind !== 'ready') return;
    let cancelled = false;
    void (async () => {
      const { data: sess } = await supabase.auth.getSession();
      if (cancelled) return;
      if (sess.session) setCurrentUserId(sess.session.user.id);
      const { data: memberRows } = await supabase.rpc('list_org_members', {
        p_org_id: load.doc.org_id
      });
      if (cancelled) return;
      if (Array.isArray(memberRows)) {
        const mapped: CommentsMember[] = (
          memberRows as Array<{
            user_id: string | null;
            display_name: string | null;
            username: string | null;
            kind: string | null;
          }>
        )
          .filter((m) => m.kind === 'active' && !!m.user_id)
          .map((m) => ({
            id: m.user_id!,
            name: m.display_name ?? m.username ?? 'Member',
            handle: m.username ?? undefined
          }))
          .sort((a, b) => a.name.localeCompare(b.name));
        setMembers(mapped);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [load]);

  // Comments fetch (re-runs on commentsTick after every mutation).
  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    void (async () => {
      const { data } = await supabase
        .from('comments')
        .select('id, parent_comment_id, author_id, body, created_at, edited_at, resolved_at')
        .eq('subject_kind', 'document')
        .eq('subject_id', id)
        .order('created_at', { ascending: true });
      if (cancelled || !data) return;
      setRawComments(
        (data as Array<{
          id: string;
          parent_comment_id: string | null;
          author_id: string;
          body: string;
          created_at: string;
          edited_at: string | null;
          resolved_at: string | null;
        }>).map((c) => ({
          id: c.id,
          parentCommentId: c.parent_comment_id,
          authorId: c.author_id,
          body: c.body,
          createdAt: c.created_at,
          editedAt: c.edited_at,
          resolvedAt: c.resolved_at
        }))
      );
    })();
    return () => {
      cancelled = true;
    };
  }, [id, commentsTick]);

  // Derived: name lookup for member-keyed rows (comments).
  const nameByUserId = useMemo(() => {
    const m = new Map<string, string>();
    members.forEach((mem) => m.set(mem.id, mem.name));
    return m;
  }, [members]);

  const commentRows: CommentRow[] = useMemo(
    () =>
      rawComments.map((c) => ({
        ...c,
        authorName: nameByUserId.get(c.authorId) ?? 'Unknown'
      })),
    [rawComments, nameByUserId]
  );

  // Comments mutation handlers.
  const onAddComment = useCallback(
    async (input: { body: string; parentCommentId: string | null }) => {
      if (!id || !currentUserId) return;
      const { error } = await supabase.from('comments').insert({
        subject_kind: 'document',
        subject_id: id,
        parent_comment_id: input.parentCommentId,
        body: input.body,
        author_id: currentUserId
      });
      if (error) setToast(error.message ?? 'Comment failed.');
      setCommentsTick((n) => n + 1);
    },
    [id, currentUserId]
  );

  const onEditComment = useCallback(
    async (input: { id: string; body: string }) => {
      const { error } = await supabase
        .from('comments')
        .update({ body: input.body, edited_at: new Date().toISOString() })
        .eq('id', input.id);
      if (error) setToast(error.message ?? 'Edit failed.');
      setCommentsTick((n) => n + 1);
    },
    []
  );

  const onDeleteComment = useCallback(async (commentId: string) => {
    const { error } = await supabase.from('comments').delete().eq('id', commentId);
    if (error) setToast(error.message ?? 'Delete failed.');
    setCommentsTick((n) => n + 1);
  }, []);

  const onResolveComment = useCallback(
    async (commentId: string) => {
      if (!currentUserId) return;
      const { error } = await supabase
        .from('comments')
        .update({
          resolved_at: new Date().toISOString(),
          resolved_by: currentUserId
        })
        .eq('id', commentId);
      if (error) setToast(error.message ?? 'Resolve failed.');
      setCommentsTick((n) => n + 1);
    },
    [currentUserId]
  );

  const onReopenComment = useCallback(async (commentId: string) => {
    const { error } = await supabase
      .from('comments')
      .update({ resolved_at: null, resolved_by: null })
      .eq('id', commentId);
    if (error) setToast(error.message ?? 'Reopen failed.');
    setCommentsTick((n) => n + 1);
  }, []);

  // Page-level Cmd/Ctrl+S handler — saves title + body regardless of where
  // focus is (title field, body editor, properties panel input, comments
  // composer, etc.). The body editor stops propagation on its own Cmd+S
  // handler so we never double-fire when focus is inside the editor.
  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      const isSaveShortcut =
        (e.metaKey || e.ctrlKey) && (e.key === 's' || e.key === 'S');
      if (!isSaveShortcut) return;
      e.preventDefault();
      if (!dirty || saveState === 'saving' || saveState === 'conflict') return;
      void onSave();
    }
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [dirty, saveState, onSave]);

  // In a cover the FullScreenCover header owns close/navigation, so the Savebar
  // "← Documents" link (a router Link) is suppressed — following it would change
  // the route behind the cover.
  const backTarget =
    surface === 'cover' ? undefined : { to: '/documents', label: 'Documents' };

  const onPropertiesError = useCallback((msg: string) => setToast(msg), []);
  const documentProperties = useDocumentProperties({
    documentId: id ?? '',
    orgId: load.kind === 'ready' ? load.doc.org_id : '',
    onError: onPropertiesError
  });

  if (load.kind === 'loading') {
    return (
      <section className="wid-page" data-testid="document-editor-v2" data-status="loading">
        <Skeleton width={320} height={32} />
        <Skeleton width="100%" height={20} />
      </section>
    );
  }

  if (load.kind === 'notfound') {
    return (
      <section className="wid-page" data-testid="document-editor-v2" data-status="notfound">
        <EmptyState data-testid="document-editor-v2-notfound">
          Document not found.
        </EmptyState>
      </section>
    );
  }

  if (load.kind === 'error') {
    return (
      <section className="wid-page" data-testid="document-editor-v2" data-status="error">
        <EmptyState data-testid="document-editor-v2-error">{load.message}</EmptyState>
      </section>
    );
  }

  return (
    <section
      ref={shellRef}
      className="wid-page"
      data-testid="document-editor-v2"
      data-status="ready"
      data-surface={surface}
      data-doc-id={load.doc.id}
    >
      {ideBuild ? (
        <DocumentSavebarIDE
          base={{
            back: backTarget,
            saveState,
            dirty,
            onSave,
            onHistoryOpen: toggleHistory,
            historyOpen,
            mode: editorMode,
            onModeChange: setEditorMode,
            railOpen,
            onRailToggle: toggleInspector,
            testIdPrefix: 'document-editor-v2'
          }}
        />
      ) : (
        <Savebar
          back={backTarget}
          saveState={saveState}
          dirty={dirty}
          onSave={onSave}
          onHistoryOpen={toggleHistory}
          historyOpen={historyOpen}
          mode={editorMode}
          onModeChange={setEditorMode}
          railOpen={railOpen}
          onRailToggle={toggleInspector}
          testIdPrefix="document-editor-v2"
        />
      )}

      <EditorShell
        data-testid="document-editor-v2-shell"
        data-rail-open={railOpen || historyOpen ? 'true' : 'false'}
        data-chat-open="false"
      >
        <EditorShell.Pane>
          <EditorShell.Stage>
            <EditorShell.Canvas>
              <PropertiesPanel
                documentId={load.doc.id}
                orgId={load.doc.org_id}
                typeId={load.doc.type_id}
                editable={editorMode === 'edit'}
                onError={onPropertiesError}
                onSetType={editor.setType}
                load={documentProperties.load}
                commitValue={documentProperties.commitValue}
                createOption={documentProperties.createOption}
                naked
                variant="bar"
                testIdPrefix="document-editor-v2-property-bar"
              />
              <header className="wid-header">
                {agentArchived ? (
                  <AgentArchiveBanner mode="archive-document" />
                ) : (
                  <EditorTitle
                    id="document-editor-v2-title"
                    value={titleDraft}
                    onChange={(e) => setTitleDraft(e.target.value)}
                    data-testid="document-editor-v2-title"
                    selectAllOnFocus={titleDraft === UNTITLED_DOCUMENT_TITLE}
                  />
                )}
              </header>
              <MarkdownEditor
                ref={bodyRef}
                value={bodyDraft}
                onChange={setBodyDraft}
                onSave={() => void onSave()}
                saveState={saveState}
                mode={editorMode}
                onActiveFormatsChange={setActiveFormats}
                dataTestId="document-editor-v2-body"
              />
            </EditorShell.Canvas>
          </EditorShell.Stage>
          <FormattingToolbar
            visible={editorMode === 'edit'}
            onInsert={(action) => bodyRef.current?.insertSyntax(action)}
            activeActions={activeFormats}
            className="de-v2-float-toolbar"
            dataTestId="document-editor-v2-format-toolbar"
          />
        </EditorShell.Pane>
        <InspectorTabs
          open={railOpen}
          onClose={closeInspector}
          kicker="Details"
          title={titleDraft}
          activeTab={inspectorTab}
          onTabChange={(v) =>
            setInspectorTab(v as 'properties' | 'work-items' | 'comments')
          }
          ariaLabel="Document inspector"
          dataTestId="document-editor-v2-inspector"
          wide
          tabs={[
            {
              value: 'properties',
              label: 'Properties',
              panel: (
                <SheetSection label="Properties">
                  <PropertiesPanel
                    documentId={load.doc.id}
                    orgId={load.doc.org_id}
                    typeId={load.doc.type_id}
                    editable={editorMode === 'edit'}
                    onError={onPropertiesError}
                    onSetType={editor.setType}
                    load={documentProperties.load}
                    commitValue={documentProperties.commitValue}
                    createOption={documentProperties.createOption}
                    naked
                  />
                  <DocumentProjectsSection
                    documentId={load.doc.id}
                    orgId={load.doc.org_id}
                    editable={editorMode === 'edit'}
                    onError={onPropertiesError}
                  />
                </SheetSection>
              )
            },
            {
              value: 'work-items',
              label: 'Work items',
              panel: (
                <AttachedWorkItemsPanel
                  documentId={load.doc.id}
                  orgId={load.doc.org_id}
                  onError={(msg) => setToast(msg)}
                  naked
                />
              )
            },
            {
              value: 'comments',
              label: 'Comments',
              panel: (
                <CommentsPanel
                  subjectKind="document"
                  subjectId={load.doc.id}
                  comments={commentRows}
                  currentUserId={currentUserId}
                  onAddComment={onAddComment}
                  onEditComment={onEditComment}
                  onDeleteComment={onDeleteComment}
                  onResolveComment={onResolveComment}
                  onReopenComment={onReopenComment}
                  naked
                />
              )
            }
          ]}
        />
        <HistoryDrawer
          subjectKind="document"
          subjectId={load.doc.id}
          open={historyOpen}
          onClose={closeHistory}
          liveTitle={titleDraft}
          liveBody={bodyDraft}
          vm={historyVM}
        />

        {/*
         * IDE-only agent-write listener. Split into its own component so
         * `useAgentWriteSubscription` (which calls `useAgentSession`, requiring
         * the IDE-only AgentSessionProvider) is NEVER invoked in the plain web
         * build. Renders nothing — it only wires the subscription.
         */}
        {ideBuild ? (
          <DocumentAgentWriteListener
            documentId={load.doc.id}
            onAgentWrite={onAgentWrite}
          />
        ) : null}

        {/* Agent-write toast slot — bottom-right of the editor shell. The
         * AgentWriteToast wrapper owns no positioning; the slot below does. */}
        {agentToastShown ? (
          <div className="dev2-agent-toast-slot">
            <AgentWriteToast
              mode="update-document"
              onDismiss={() => setAgentToastShown(false)}
            />
          </div>
        ) : null}
      </EditorShell>

      {toast ? (
        <Toast onDismiss={() => setToast(null)} className="dev2-toast">
          {toast}
        </Toast>
      ) : null}
    </section>
  );
}


interface DocumentAgentWriteListenerProps {
  documentId: string;
  onAgentWrite: (frame: McpWriteCommittedFrame) => void;
}

/**
 * IDE-only subscriber to committed agent writes targeting the OPEN document
 * (Phase 3, Task 3.2). Mounted only in the IDE build (the caller gates on
 * `isIDE()`), so `useAgentWriteSubscription` — which calls `useAgentSession`,
 * requiring the IDE-only `AgentSessionProvider` — is never invoked in the
 * plain web build. The hook filters frames to `document` + this id, so the
 * parent's handler only ever sees writes that landed on the open document.
 * Renders nothing.
 */
function DocumentAgentWriteListener({
  documentId,
  onAgentWrite
}: DocumentAgentWriteListenerProps) {
  useAgentWriteSubscription('document', documentId, onAgentWrite);
  return null;
}

/**
 * IDE-only Savebar wrapper for the Document view. Split out so `useAgentSession`
 * is not called in the plain web build.
 */
function DocumentSavebarIDE(props: {
  base: ComponentProps<typeof Savebar>;
}) {
  return <Savebar {...props.base} />;
}

/** Route entry: `/document-editor/:id` (and legacy `/documents/:id`). */
export function DocumentEditor() {
  const { id } = useParams<{ id: string }>();
  return <DocumentEditorView documentId={id} surface="route" />;
}

